import { ComponentType, ReactNode } from "react";
import { GenericField } from "@/lib/Components/Form/Fields/GenericField";
import { MultiSelect, MultiSelectOption } from "@/components/ui/multi-select";
import { useField } from "formik";

export function MultiSelectInput({
  label,
  name,
  options,
  className,
  tooltip,
  showSelectAll = true,
  value: controlledValue,
  onChange,
  LeadingIcon,
  disabled = false,
  getViewNode,
  lockedValues = [],
}: {
  name: string;
  label: string;
  className: string;
  options: MultiSelectOption[];
  tooltip?: string;
  showSelectAll?: boolean;
  value?: string[];
  onChange?: (values: string[]) => void;
  LeadingIcon?: ComponentType<{ className?: string }>;
  disabled?: boolean;
  getViewNode?: (option: MultiSelectOption) => ReactNode;
  lockedValues?: string[];
}) {
  const [field, , { setValue }] = useField<string[]>(name);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : field.value;

  const selectedOptions = (currentValue ?? [])
    .map((val) => options.find((opt) => opt.value === val))
    .filter((opt): opt is MultiSelectOption => opt !== undefined);

  const viewNode = getViewNode ? (
    selectedOptions.length > 0 ? (
      <span className="flex flex-wrap gap-x-1">
        {selectedOptions.map((opt, index) => (
          <span key={opt.value}>
            {getViewNode(opt)}
            {index < selectedOptions.length - 1 ? "," : ""}
          </span>
        ))}
      </span>
    ) : (
      <span>--</span>
    )
  ) : (
    <span>{selectedOptions.map((opt) => opt.label).join(", ") || "--"}</span>
  );

  return (
    <GenericField
      name={name}
      label={label}
      viewNode={viewNode}
      className={className}
      tooltip={tooltip}
    >
      {() => (
        <MultiSelect
          value={currentValue}
          options={options}
          showSelectAll={showSelectAll}
          LeadingIcon={LeadingIcon}
          disabled={disabled}
          lockedValues={lockedValues}
          onValueChange={(newVal) => {
            if (onChange) {
              onChange(newVal);
            } else {
              setValue(newVal);
            }
          }}
        />
      )}
    </GenericField>
  );
}
